// Sprint 9.5 — AdherenceBars
// SVG inline bar chart distribuzione aderenza per buckets.
//
// 5 buckets verticali (0-20, 21-40, 41-60, 61-80, 81-100).
// Click bucket → invoca onBucketClick(label).
// Hover → tooltip con count assoluto.

import { useState } from 'react';
import { AdherenceBucket } from '../lib/api';

interface AdherenceBarsProps {
  buckets: AdherenceBucket[];
  totalPatients: number;
  avgAdherence: number;
  onBucketClick?: (label: string) => void;
}

function colorForBucket(label: string): string {
  switch (label) {
    case '0-20': return '#c0392b';
    case '21-40': return '#e67e22';
    case '41-60': return '#f1c40f';
    case '61-80': return '#5fb37e';
    case '81-100': return '#0b8a3e';
    default: return '#888';
  }
}

export function AdherenceBars({ buckets, totalPatients, avgAdherence, onBucketClick }: AdherenceBarsProps) {
  const [hover, setHover] = useState<{ label: string; count: number; x: number; y: number } | null>(null);

  const PAD = 24;
  const CHART_H = 200;
  const CHART_W = 460;
  const BAR_W = 56;
  const BAR_GAP = 24;
  const totalW = buckets.length * BAR_W + (buckets.length - 1) * BAR_GAP;
  const offsetX = (CHART_W - totalW) / 2;

  const maxCount = Math.max(1, ...buckets.map(b => b.count));

  if (totalPatients === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--mute, #888)', fontSize: 13 }}>
        Aggiungi pazienti per visualizzare la distribuzione aderenza.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="100%"
        viewBox={`0 0 ${CHART_W} ${CHART_H + PAD * 2}`}
        style={{ display: 'block' }}
        role="img"
        aria-label="Distribuzione aderenza per bucket"
      >
        {/* avg line */}
        <line
          x1={PAD}
          x2={CHART_W - PAD}
          y1={PAD + CHART_H * (1 - avgAdherence / 100)}
          y2={PAD + CHART_H * (1 - avgAdherence / 100)}
          stroke="var(--mute, #888)"
          strokeWidth={1}
          strokeDasharray="3,3"
        />
        <text
          x={CHART_W - PAD}
          y={PAD + CHART_H * (1 - avgAdherence / 100) - 4}
          fontSize={10}
          fontFamily="var(--mono, monospace)"
          fill="var(--mute, #888)"
          textAnchor="end"
        >
          media {avgAdherence}%
        </text>

        {/* Bars */}
        {buckets.map((b, i) => {
          const x = offsetX + i * (BAR_W + BAR_GAP);
          const h = (b.count / maxCount) * (CHART_H - 30);
          const y = PAD + CHART_H - h;
          const color = colorForBucket(b.range_label);
          const isHovered = hover && hover.label === b.range_label;
          return (
            <g
              key={b.range_label}
              onMouseEnter={() => setHover({ label: b.range_label, count: b.count, x: x + BAR_W / 2, y: y - 4 })}
              onMouseLeave={() => setHover(null)}
              onClick={() => onBucketClick?.(b.range_label)}
              style={{ cursor: onBucketClick ? 'pointer' : 'default' }}
              role={onBucketClick ? 'button' : undefined}
              aria-label={`${b.range_label}% aderenza: ${b.count} pazienti`}
            >
              <rect
                x={x}
                y={y}
                width={BAR_W}
                height={h}
                rx={4}
                ry={4}
                fill={color}
                opacity={isHovered ? 1 : 0.85}
                stroke={isHovered ? 'var(--ink, #111)' : 'none'}
                strokeWidth={2}
              />
              <text
                x={x + BAR_W / 2}
                y={y - 6}
                fontSize={13}
                fontWeight={700}
                fill="var(--ink, #111)"
                textAnchor="middle"
              >
                {b.count}
              </text>
              <text
                x={x + BAR_W / 2}
                y={PAD + CHART_H + 18}
                fontSize={11}
                fontFamily="var(--mono, monospace)"
                fontWeight={600}
                fill="var(--ink-3, #666)"
                textAnchor="middle"
              >
                {b.range_label}%
              </text>
            </g>
          );
        })}
      </svg>
      {hover && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            left: `${(hover.x / CHART_W) * 100}%`,
            top: `${(hover.y / (CHART_H + PAD * 2)) * 100}%`,
            transform: 'translate(-50%, -120%)',
            background: 'var(--ink, #111)',
            color: '#fff',
            padding: '5px 9px',
            borderRadius: 5,
            fontSize: 11,
            fontFamily: 'var(--mono, monospace)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
          }}
        >
          {hover.label}%: {hover.count} pazienti {onBucketClick && '· click per drill-down'}
        </div>
      )}
    </div>
  );
}
